import { useState, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  Chip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const GITEA_SLUG_ANNOTATION = 'gitea.kyndemo.live/repo-slug';

export const isGiteaPromoteAvailable = (entity: any) =>
  Boolean(entity?.metadata?.annotations?.[GITEA_SLUG_ANNOTATION]);

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0),
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  arrow: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0.5),
  },
  result: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.85rem',
  },
  ok: {
    background: theme.palette.type === 'dark'
      ? 'rgba(76,175,80,0.15)'
      : 'rgba(76,175,80,0.08)',
    color: theme.palette.type === 'dark' ? '#81c784' : '#2e7d32',
  },
  err: {
    background: theme.palette.type === 'dark'
      ? 'rgba(244,67,54,0.15)'
      : 'rgba(244,67,54,0.08)',
    color: theme.palette.type === 'dark' ? '#ef9a9a' : '#c62828',
  },
}));

type PromoteResult = { ok: boolean; message: string; prUrl?: string };

type PromoAction = {
  label: string;
  head: string;
  base: string;
  description: string;
};

const PROMOTIONS: PromoAction[] = [
  {
    label: 'Promote to Staging',
    head: 'dev',
    base: 'staging',
    description: 'Opens a dev → staging PR. Integration tests gate the merge.',
  },
  {
    label: 'Promote to Production',
    head: 'staging',
    base: 'prod',
    description: 'Opens a staging → prod PR. Requires 1 approval after tests pass.',
  },
];

export const GiteaPromoteContent = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const theme = useTheme();
  const discovery = useApi(discoveryApiRef);
  const fetchApi  = useApi(fetchApiRef);

  const [loading,  setLoading]  = useState<string | null>(null);
  const [results,  setResults]  = useState<Record<string, PromoteResult>>({});

  const slug = entity?.metadata?.annotations?.[GITEA_SLUG_ANNOTATION] ?? '';
  const [owner, repo] = slug.split('/');

  const authedFetch = useCallback(async (url: string, init?: RequestInit) => {
    const baseUrl = await discovery.getBaseUrl('gitea-promote');
    return fetchApi.fetch(`${baseUrl}${url}`, init);
  }, [discovery, fetchApi]);

  const handlePromote = useCallback(async (action: PromoAction) => {
    const key = `${action.head}→${action.base}`;
    setLoading(key);
    setResults(r => ({ ...r, [key]: undefined as any }));
    try {
      const resp = await authedFetch('/open-pr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repoOwner: owner,
          repoName: repo,
          head: action.head,
          base: action.base,
          title: `chore(platform): promote ${action.head} → ${action.base}`,
          body: [
            `## Platform Promotion: \`${repo}\``,
            '',
            `Promoting \`${action.head}\` → \`${action.base}\` initiated from Backstage.`,
            '',
            'Integration tests must pass and 1 approval is required before merge.',
          ].join('\n'),
        }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setResults(r => ({ ...r, [key]: { ok: true, message: `PR #${data.prNumber} opened`, prUrl: data.prUrl } }));
      } else {
        setResults(r => ({ ...r, [key]: { ok: false, message: data.error || `HTTP ${resp.status}` } }));
      }
    } catch (e: any) {
      setResults(r => ({ ...r, [key]: { ok: false, message: e?.message || 'Request failed' } }));
    } finally {
      setLoading(null);
    }
  }, [authedFetch, owner, repo]);

  if (!owner || !repo) {
    return null;
  }

  return (
    <InfoCard
      title="Environment Promotion"
      subheader={
        <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
          Platform-managed GitOps promotions for <strong>{slug}</strong>
        </Typography>
      }
    >
      <Box>
        {PROMOTIONS.map((action, i) => {
          const key = `${action.head}→${action.base}`;
          const result = results[key];
          const isLoading = loading === key;
          return (
            <Box key={key}>
              {i > 0 && <Divider />}
              <Box className={classes.row}>
                <Box className={classes.label}>
                  <Chip label={action.head} size="small" variant="outlined" />
                  <span className={classes.arrow}>→</span>
                  <Chip label={action.base} size="small" variant="outlined" />
                  <Typography variant="caption" style={{ color: theme.palette.text.secondary, marginLeft: 8 }}>
                    {action.description}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <CallSplitIcon />}
                  disabled={!!loading}
                  onClick={() => handlePromote(action)}
                >
                  {action.label}
                </Button>
              </Box>
              {result && (
                <Box className={`${classes.result} ${result.ok ? classes.ok : classes.err}`}>
                  {result.message}
                  {result.ok && result.prUrl && (
                    <>
                      {' — '}
                      <a href={result.prUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                        View PR <OpenInNewIcon style={{ fontSize: 11, verticalAlign: 'middle' }} />
                      </a>
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </InfoCard>
  );
};
